import { FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, createStyles, Theme, useTheme } from '@material-ui/core/styles';
import PetSearch from '../PetSearch';
import { NewBookingAlert } from '@spike/new-booking-model';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { fetchAlertsThunk } from '@spike/new-bookings-action';
import DateSelector from '../DateSelector';
import MultipleDateSelector from '../DateSelector/MultipleDateSelector';
import { AddOnDate, Discount as DiscountModel, itemTypes, versionPetAndServices } from 'model/NewMultipleBooking';
import ProductSelector from '../ProductSelector';
import { Product } from '@spike/product-model';
import AddOnSelector from '../AddOnSelector';
import AddOnService from '@spike/addon-service-model';
import { TextFieldNoLabel } from 'components/UI';
import QuantityAndPrice from '../QuantityAndPrice';
import clsx from 'clsx';
import ExtraStaffSelector from '../StaffSelector/ExtraStaffSelector';
import Discount from '../Discount';
import FooterComponent from '../FooterComponent';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import useNonInitialEffect from '@versiondos/hooks';
import { noop } from 'lodash';
import { SingleSaleTableItemTaxesSelector } from 'components/Payments/SingleSale/Table/Item/Taxes/Selector/SingleSaleTableItemTaxesSelector';
import { showError } from '@spike/notifications-action';
import { getTrackedOnHand, insufficientStockMessage } from 'utils/productOnHand';

interface Props {
    parentId: number;
    petId?: number;
    versionPetAndServices?: versionPetAndServices;
    date?: Moment;
    type?: itemTypes;
    product?: Product;
    addOn?: AddOnService;
    custom?: string;
    discount?: DiscountModel;
    quantity?: number;
    amount?: number;
    taxId?: number;
    staffId?: number;
    itemsUsed?: Array<number>;
    loading?: boolean;
    petWithoutSelect?: boolean;
    dates?: Array<AddOnDate>;
    changedDate?: boolean;
    primaryServiceStaffId?: number;
    petBusinessAreas?: Array<string>;
    onChangeDate?: (value: Moment) => void;
    onBook: (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: DiscountModel,
        quantity?: number,
        amount?: number,
        taxId?: number,
        dates?: Array<Moment>
    ) => void;
    onRemove?: (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string
    ) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
        },
        sectionContainer: {
            paddingTop: 18,
            [theme.breakpoints.up('md')]: {
                paddingTop: 20
            }
        },
        divide: {
            borderBottom: '1px solid #D4D4D4'
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginTop: 8,
            marginBottom: 10,
            [theme.breakpoints.up('md')]: {
                marginBottom: 12
            }
        },
        total: {
            fontWeight: 400,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        },
        quantityAndPriceContainer: {
            display: 'flex',
            width: '100%'
        },
        quantityAndPriceContainerVertical: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        marginTop: {
            marginTop: 10
        },
        quantity: {
            width: '44%',
            marginRight: 20
        },
        discount: {
            width: '52%'
        },
        quantityVertical: {
            width: '100%',
            marginBottom: 30
        },
        discountVertical: {
            width: '100%'
        },
        totalContainer: {
            height: '15%',
            borderTop: '1px solid #D4D4D4',
            marginTop: 11,
            marginBottom: 5
        },
        content: {
            margin: '0px 26px 0px 26px',
            [theme.breakpoints.up('md')]: {
                margin: '0px 30px 0px 30px'
            }
        },
        taxSelector: {
            marginTop: 0,
            width: '33%',
            [theme.breakpoints.down('sm')]: {
                width: '100%'
            }
        }
    })
);

export const AddOrEditComplementaryItem: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const marketplace = useMarketplace();
    const timezone = marketplace.schedule.timeZone;
    const isAddOnAdd = props.type === itemTypes.addOn && props.versionPetAndServices === versionPetAndServices.add;

    const [date, setDate] = useState<Moment | undefined>(props.date);
    const [from, setFrom] = useState<Moment>(() => props.date?.clone() ?? moment().tz(timezone));
    const [to, setTo] = useState<Moment>(() => props.date?.clone() ?? moment().tz(timezone));
    const [selectedDates, setSelectedDates] = useState<Array<Moment>>(() =>
        props.date ? [props.date.clone().tz(timezone).startOf('day')] : []
    );
    const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(props.product);
    const [selectedAddOn, setSelectedAddOn] = useState<AddOnService | undefined>(props.addOn);
    const [custom, setCustom] = useState<string | undefined>(props.custom);
    const [quantity, setQuantity] = useState<number>(props.quantity || 1);
    const [amount, setAmount] = useState<number>(props.amount || 0);
    const [selectedTaxId, setSelectedTaxId] = useState<number | undefined>(props.taxId);
    const [staffId, setStaffId] = useState<number | undefined>(props.staffId);
    const [changedDate, setChangedDate] = useState<boolean>(!!props.changedDate);
    const [discount, setDiscount] = useState<DiscountModel>(
        props.discount || {
            type: 'amount',
            value: 0
        }
    );
    const [disabledButton, setDisabledButton] = useState<boolean>(false);
    const [defaultAmount, setDefaultAmount] = useState<string | undefined>(undefined);
    const [defaultDiscount, setDefaultDiscount] = useState<string | undefined>(undefined);

    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);

    useEffect(() => {
        if (date) {
            dispatch(
                fetchAlertsThunk(
                    apiClientWrapper,
                    props.parentId || 0,
                    props.petId || 0,
                    date.clone().tz('utc'),
                    false,
                    undefined,
                    staffId,
                    undefined
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.date, date, selectedProduct, selectedAddOn, custom, staffId]);

    useNonInitialEffect(() => {
        if (props.date?.format('L') !== date?.format('L') && props.type === itemTypes.addOn) {
            props.onChangeDate && props.onChangeDate(date!);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    useEffect(() => {
        if (isNaN(Number(discount.value))) {
            setDisabledButton(true);
        } else {
            setDisabledButton(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discount]);

    const changeQuantityHandler = (nextQuantity: number) => {
        if (props.type === itemTypes.product && selectedProduct && nextQuantity > quantity) {
            const onHand = getTrackedOnHand(selectedProduct);

            if (onHand !== undefined && nextQuantity > onHand) {
                dispatch(showError(insufficientStockMessage(onHand)));
                return;
            }
        }

        setQuantity(nextQuantity);
    };

    useNonInitialEffect(() => {
        if (props.type !== itemTypes.custom) {
            const onHand = props.type === itemTypes.product ? getTrackedOnHand(selectedProduct) : undefined;
            const nextQuantity = onHand !== undefined && onHand < 1 ? onHand : 1;

            if (onHand !== undefined && nextQuantity < 1) {
                dispatch(showError(insufficientStockMessage(onHand)));
            }

            setQuantity(nextQuantity);
            setDiscount({
                type: 'amount',
                value: 0
            });
            setDefaultDiscount('0.00');
            setStaffId(
                props.type === itemTypes.addOn && props.primaryServiceStaffId != null
                    ? props.primaryServiceStaffId
                    : undefined
            );
        }

        if (props.type === itemTypes.addOn) {
            setAmount(selectedAddOn?.price ? Number(selectedAddOn?.price) : 0);
            setDefaultAmount(selectedAddOn?.price ? selectedAddOn?.price?.toString() : '0.00');
        }

        if (props.type === itemTypes.product) {
            setAmount(
                selectedProduct?.businessProduct?.pricing?.price
                    ? Number(selectedProduct?.businessProduct?.pricing?.price)
                    : 0
            );
            setDefaultAmount(
                selectedProduct?.businessProduct?.pricing?.price
                    ? selectedProduct?.businessProduct?.pricing?.price?.toString()
                    : '0.00'
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAddOn, selectedProduct, custom, props.type, props.primaryServiceStaffId]);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            if (defaultAmount || defaultDiscount) {
                setDefaultAmount(undefined);
                setDefaultDiscount(undefined);
            }
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultAmount, defaultDiscount]);

    useEffect(() => {
        const missingItem = !selectedAddOn && !selectedProduct && !custom;
        const missingAddOnDates = isAddOnAdd && selectedDates.length === 0;
        setDisabledButton(missingItem || missingAddOnDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAddOn, selectedProduct, custom, isAddOnAdd, selectedDates.length]);

    useEffect(() => {
        setSelectedTaxId(props.taxId);
    }, [props.taxId]);

    useNonInitialEffect(() => {
        setDiscount({
            type: 'amount',
            value: 0
        });
        setDefaultDiscount('0.00');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quantity, amount]);

    const handlerChangeDate = (value: Moment) => {
        setDate(value);
        if (date?.format('L') !== value.format('L')) {
            setChangedDate(true);
        }
    };

    const changePeriodHandler = (nextFrom: Moment, nextTo: Moment) => {
        setFrom(nextFrom.clone());
        setTo(nextTo.clone());
        setDate(nextFrom.clone());
    };

    const changeSelectedDatesHandler = (datesOrUpdater: SetStateAction<Array<Moment>>) => {
        setSelectedDates(prev => {
            const next = typeof datesOrUpdater === 'function' ? datesOrUpdater(prev) : datesOrUpdater;
            const normalized = next.map(d => d.clone().tz(timezone).startOf('day'));
            if (normalized.length > 0) {
                const firstDate = normalized[0];
                setDate(
                    moment.tz(
                        `${firstDate.format('YYYY-MM-DD')} ${from.format('LT').toUpperCase()}`,
                        'YYYY-MM-DD hh:mm A',
                        timezone
                    )
                );
            }
            return normalized;
        });
    };

    const buildDatesWithTime = (): Array<Moment> =>
        selectedDates.map(selectedDate =>
            moment.tz(
                `${selectedDate.format('YYYY-MM-DD')} ${from.format('LT').toUpperCase()}`,
                'YYYY-MM-DD hh:mm A',
                timezone
            )
        );

    const handlerBook = () => {
        if (isAddOnAdd && selectedDates.length > 0) {
            props.onBook(
                undefined,
                props.petId,
                undefined,
                selectedAddOn,
                undefined,
                staffId,
                discount,
                quantity,
                amount,
                undefined,
                buildDatesWithTime()
            );
            return;
        }

        props.onBook(
            date,
            props.petId,
            props.type === itemTypes.product ? selectedProduct : undefined,
            props.type === itemTypes.addOn ? selectedAddOn : undefined,
            props.type === itemTypes.custom ? custom : undefined,
            staffId,
            discount,
            quantity,
            amount,
            props.type === itemTypes.custom ? selectedTaxId : undefined
        );
    };

    const handleRemove = () => {
        props.onRemove &&
            props.onRemove(
                date,
                props.petId,
                props.type === itemTypes.product ? selectedProduct : undefined,
                props.type === itemTypes.addOn ? selectedAddOn : undefined,
                props.type === itemTypes.custom ? custom : undefined
            );
    };

    const getTotal = () => {
        const total: number = quantity * amount;
        if (discount.type === 'percentage') {
            const totalAmount = discount.value ? ((100 - discount.value) * total) / 100 : total;
            return totalAmount.toFixed(2);
        } else {
            const totalAmount = discount.value ? total - discount.value : total;
            return totalAmount.toFixed(2);
        }
    };

    const petsView = (
        <Box className={classes.sectionContainer}>
            <PetSearch
                parentID={props.parentId}
                petId={props.petId}
                multiplePets={true}
                readOnly={true}
                hideSelect={props.petWithoutSelect || false}
                handlerPet={noop}
            />
        </Box>
    );

    const dateView = isAddOnAdd ? (
        <Box className={classes.sectionContainer}>
            <MultipleDateSelector
                from={from}
                to={to}
                selectedDates={selectedDates}
                labelDate="Date"
                labelTimeStart="Start Time"
                hideEndTime
                isRecurrent={false}
                frequencyOptions={[]}
                onChangeFrequencyDuration={noop}
                alerts={alerts?.filter(alert => alert.alertType === 'unified')}
                onChangeSelectedDates={changeSelectedDatesHandler}
                onChangePeriod={changePeriodHandler}
            />
        </Box>
    ) : (
        <Box className={classes.sectionContainer}>
            <DateSelector
                date={date!}
                withoutBorder
                showAlertBusinessHour={
                    alerts && alerts?.filter(alert => alert.alertType === 'date_time')?.length > 0 ? true : false
                }
                multiplePet={true}
                onChangeDate={handlerChangeDate}
                alerts={alerts?.filter(alert => alert.alertType === 'unified')}
            />
        </Box>
    );

    const productsView = (
        <Box className={classes.sectionContainer}>
            <ProductSelector
                productsUsed={props.itemsUsed}
                selectedProduct={selectedProduct}
                onSelectProduct={setSelectedProduct}
            />
        </Box>
    );

    const addOnView = (
        <Box className={classes.sectionContainer}>
            <AddOnSelector
                changedDate={changedDate}
                addOnsUsed={props.itemsUsed}
                selectedAddOn={selectedAddOn}
                date={date}
                dates={props.dates}
                selectedDates={isAddOnAdd ? selectedDates : undefined}
                petBusinessAreas={props.petBusinessAreas}
                onSelectAddOn={setSelectedAddOn}
            />
        </Box>
    );

    const customView = (
        <Box className={classes.sectionContainer}>
            <Typography className={classes.title}>Description</Typography>
            <TextFieldNoLabel
                placeholder="Enter Description"
                value={custom || ''}
                onChange={setCustom}
            />
        </Box>
    );

    const taxCustomView = (
        <Box className={classes.sectionContainer}>
            <Typography className={classes.title}>Tax</Typography>
            <SingleSaleTableItemTaxesSelector
                options={marketplace.taxes}
                selectedTaxIds={selectedTaxId != null ? [selectedTaxId] : []}
                className={clsx(classes.marginTop, classes.taxSelector)}
                textAlign="left"
                onSelect={tax => setSelectedTaxId(tax?.id)}
            />
        </Box>
    );

    const totalView = (
        <Box className={classes.sectionContainer}>
            <Typography className={classes.title}>Total</Typography>
            <Typography className={classes.total}>{`$${getTotal()}`}</Typography>
        </Box>
    );

    const quantityPriceAndDiscountView = (
        <Box className={clsx(classes.sectionContainer, classes.marginTop)}>
            <Box className={isSmall ? classes.quantityAndPriceContainerVertical : classes.quantityAndPriceContainer}>
                <Box className={isSmall ? classes.quantityVertical : classes.quantity}>
                    <QuantityAndPrice
                        quantity={quantity}
                        amount={amount}
                        default={defaultAmount}
                        onChangeQuantity={changeQuantityHandler}
                        onChangeAmount={(value: number | undefined) => setAmount(value || 0)}
                    />
                </Box>
                <Box className={isSmall ? classes.discountVertical : classes.discount}>
                    <Discount
                        discount={discount}
                        max={
                            discount.type === 'percentage' ? 100 : discount.type === 'amount' ? amount * quantity : 0.5
                        }
                        default={defaultDiscount}
                        onChangeDiscount={setDiscount}
                    />
                </Box>
            </Box>
        </Box>
    );

    const staffView = (
        <Box className={classes.sectionContainer}>
            <ExtraStaffSelector
                selectedStaff={staffId}
                onSelectStaff={setStaffId}
            />
        </Box>
    );

    return (
        <Box className={classes.container}>
            <OverlayScrollbarsComponent style={{ height: '100%' }}>
                <Box className={classes.content}>
                    {petsView}
                    {dateView}
                    {props.type === itemTypes.product && productsView}
                    {props.type === itemTypes.addOn && addOnView}
                    {props.type === itemTypes.custom && customView}
                    {(selectedProduct || selectedAddOn || custom) && quantityPriceAndDiscountView}
                    {(selectedProduct || selectedAddOn || custom) && props.type === itemTypes.custom && taxCustomView}
                    {(selectedProduct || selectedAddOn || custom) && totalView}
                    {(selectedProduct || selectedAddOn || custom) && staffView}
                </Box>
            </OverlayScrollbarsComponent>
            <Box className={classes.totalContainer}>
                <FooterComponent
                    loading={props.loading || false}
                    buttonText="Save"
                    withoutTotal={true}
                    disabledButton={disabledButton}
                    onBook={handlerBook}
                    onRemove={props.versionPetAndServices === versionPetAndServices.edit ? handleRemove : undefined}
                />
            </Box>
        </Box>
    );
};

export default AddOrEditComplementaryItem;
